import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Link from '@mui/joy/Link';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Chip from '@mui/joy/Chip';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import CircularProgress from '@mui/joy/CircularProgress';
import { clientsApi } from '../../api/clients.api';
import { Client } from '../../types/client';
import { Sale } from '../../types/sale';
import { Address } from '../../types/address';
import { addressesApi } from '../../api/addresses.api';
import AddressList from '../../components/addresses/AddressList';
import MessageList from '../../components/messages/MessageList';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import { formatRecordReference } from '../../utils/formatRecordReference';
import { formatDisplayDate } from '../../utils/formatDisplayDate';

const ClientDetail: React.FC = () => {
  const { reference } = useParams<{ reference?: string }>();
  const clientReference = reference as string | undefined;
  const [client, setClient] = useState<Client | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState<Address[]>([]);

  useEffect(() => {
    if (clientReference) {
      loadClientData(clientReference);
    }
  }, [clientReference]);

  const loadClientData = async (ref: string) => {
    try {
      setLoading(true);
      const [clientData, addressesData, salesData] = await Promise.all([
        clientsApi.getOne(ref),
        addressesApi.getByClientReference(ref),
        clientsApi.getSales(ref),
      ]);
      setClient(clientData);
      setAddresses(Array.isArray(addressesData) ? addressesData : []);
      setSales(Array.isArray(salesData) ? salesData : []);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saleStatusColor = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    if (status === 'completed') return 'success';
    if (status === 'cancelled') return 'danger';
    return 'warning';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!client) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Cliente no encontrado</Typography>
      </Box>
    );
  }

  return (
    <PageStack sx={{ maxWidth: LAYOUT.detailMaxWidth, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={LAYOUT.stackDense}>
        <Typography level="h2">Detalle del cliente</Typography>
        <Button component={RouterLink} to="/clients" variant="outlined" color="neutral">
          Volver
        </Button>
      </Stack>

      <Sheet variant="outlined" sx={{ p: LAYOUT.cardPaddingLg, borderRadius: 'md', boxShadow: 'sm' }}>
        <Grid container spacing={LAYOUT.gridSpacing}>
          <Grid xs={12} sm={6}>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Nombre
            </Typography>
            <Typography level="title-md">{client.name}</Typography>
          </Grid>
          <Grid xs={12} sm={6}>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Teléfono
            </Typography>
            <Typography level="title-md">{client.phoneNumber ?? '—'}</Typography>
          </Grid>
          <Grid xs={12} sm={6}>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Fecha de creación
            </Typography>
            <Typography level="body-md">{formatDisplayDate(client.createdAt)}</Typography>
          </Grid>
          <Grid xs={12} sm={6}>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Última actualización
            </Typography>
            <Typography level="body-md">{formatDisplayDate(client.updatedAt)}</Typography>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: LAYOUT.stackTight }}>
          <Button component={RouterLink} to={`/clients/${client.reference}/edit`} color="primary">
            Editar cliente
          </Button>
          {client.phoneNumber ? (
            <Button
              component="a"
              href={`https://wa.me/${String(client.phoneNumber).replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              color="success"
              variant="soft"
            >
              Chatear por WhatsApp
            </Button>
          ) : null}
        </Stack>
      </Sheet>

      <Sheet variant="outlined" sx={{ p: LAYOUT.cardPaddingLg, borderRadius: 'md', boxShadow: 'sm' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: LAYOUT.stackTight }} flexWrap="wrap" gap={LAYOUT.stackDense}>
          <Typography level="title-lg">Historial de ventas</Typography>
          <Button component={RouterLink} to={`/sales/new?clientReference=${client.reference}`} size="sm" color="primary">
            Nueva venta
          </Button>
        </Stack>

        {sales.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            No hay ventas registradas
          </Typography>
        ) : (
          <Stack spacing={LAYOUT.stackTight}>
            {sales.map((sale) => (
              <Sheet key={sale.reference} variant="soft" sx={{ p: LAYOUT.cardPaddingDense, borderRadius: 'md' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
                  <Typography level="title-sm">
                    Venta {formatRecordReference(sale.reference)} —{' '}
                    {formatDisplayDate(sale.date)}
                  </Typography>
                  <Chip size="sm" variant="soft" color={saleStatusColor(sale.status)}>
                    {sale.status}
                  </Chip>
                </Stack>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Productos:
                </Typography>
                <List size="sm">
                  {sale.products?.map((product) => (
                    <ListItem key={product.reference}>
                      {product.name} ({product.quantity} × ${product.price.toFixed(2)})
                    </ListItem>
                  ))}
                </List>
                <Typography level="title-sm" sx={{ textAlign: 'right', mt: 1 }}>
                  Total: ${sale.total.toFixed(2)}
                </Typography>
                <Link component={RouterLink} to={`/sales/${sale.reference}`} level="body-sm">
                  Ver venta
                </Link>
              </Sheet>
            ))}
          </Stack>
        )}
      </Sheet>

      <AddressList
        addresses={addresses}
        clientReference={clientReference ?? ''}
        onAddressCreated={(address) => {
          setAddresses([...addresses, address]);
        }}
        onAddressUpdated={(updatedAddress) => {
          setAddresses(
            addresses.map((addr) => (addr.reference === updatedAddress.reference ? updatedAddress : addr)),
          );
        }}
        onAddressDeleted={async (reference) => {
          if (window.confirm('¿Estás seguro de eliminar esta dirección?')) {
            try {
              await addressesApi.delete(reference);
              setAddresses(addresses.filter((addr) => addr.reference !== reference));
            } catch (error) {
              console.error('Error deleting address:', error);
            }
          }
        }}
      />

      <MessageList clientReference={clientReference ?? ''} />
    </PageStack>
  );
};

export default ClientDetail;
